import React, { useState, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { hashPasswordClient } from '../../utils/passwordUtils';
import { useAuthPage } from '../../hooks/useAuthPage';
import { useProduct, PRODUCT_NAMES, ProductId } from '../../context/ProductContext';
import { isTinycmsFunnel } from './tinycmsPlans';
import { TinycmsLogin } from './TinycmsLogin';
import { SwallowMark } from '../common/SwallowMark';
import { TinycmsWordmark } from '../common/TinycmsWordmark';
import {
  Sparkles,
  Zap,
  Calendar,
  BarChart3,
  ArrowRight,
  BookOpenText,
  Blocks,
  LayoutGrid,
  Images,
  ShieldCheck,
} from 'lucide-react';

const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password)
  }
`;

interface LoginResponse {
  login: string;
}

/**
 * Sign-in is the first screen a pinned single-product deployment shows, so it
 * carries the same brand vocabulary as the shell (see nav/Sidebar): each product
 * gets its own mark, wordmark, promise and proof points. Colour, radii and the
 * display face come from the tokens via data-product — only wording and the mark
 * are chosen here.
 */
const BRAND: Record<
  ProductId,
  {
    tagline: string;
    headlineLead: string;
    headlineAccent: string;
    blurb: string;
    features: { icon: React.ComponentType<{ className?: string }>; text: string }[];
  }
> = {
  reach: {
    tagline: 'Your Social Media Command Center',
    headlineLead: 'Start Your Journey to',
    headlineAccent: 'Effortless Social Media',
    blurb:
      'Manage all your social media accounts, schedule posts, and grow your audience from one powerful platform.',
    features: [
      { icon: Sparkles, text: 'AI-Powered Content' },
      { icon: Calendar, text: 'Smart Scheduling' },
      { icon: BarChart3, text: 'Advanced Analytics' },
      { icon: Zap, text: 'Multi-Platform' },
    ],
  },
  atomizer: {
    tagline: 'The content library',
    headlineLead: 'Articles and media,',
    headlineAccent: 'in one library',
    blurb:
      'tinycms is the library. Research, the assembler, and atomizing an article are the Atomizer add-on.',
    features: [
      { icon: LayoutGrid, text: 'Content library' },
      { icon: Images, text: 'Media' },
      { icon: BookOpenText, text: 'Research' },
      { icon: Blocks, text: 'Assembler' },
    ],
  },
  collect: {
    tagline: 'Your Collection, Catalogued',
    headlineLead: 'Every Card You Own,',
    headlineAccent: 'Shot and Sorted',
    blurb:
      'Photograph a card and it is catalogued: cropped, identified, valued and ready to publish or list — with every original scan kept.',
    features: [
      { icon: Images, text: 'Capture to Catalogue' },
      { icon: LayoutGrid, text: 'Faceted Collection' },
      { icon: ShieldCheck, text: 'Originals Preserved' },
      { icon: BarChart3, text: 'Value Tracking' },
    ],
  },
  tinycms: {
    tagline: 'Your Site, Checked and Approved',
    headlineLead: 'Write It, Check It,',
    headlineAccent: 'Publish on Your Say-So',
    blurb:
      'Your agent drafts and edits; every article is copy-edited against your style guide and goes live only when you approve that exact version.',
    features: [
      { icon: LayoutGrid, text: 'Articles & Datasets' },
      { icon: BookOpenText, text: 'House Style Checks' },
      { icon: ShieldCheck, text: 'Approval Before Publish' },
      { icon: Sparkles, text: 'Themes Without Code' },
    ],
  },
};

/** The product mark, matched to the shell's. */
function BrandMark({ product, size }: { product: ProductId; size: number }) {
  if (product === 'atomizer') {
    // The cut stone — a gold-hairline rotated square.
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <div
          className="rotate-45 border border-cmp-bronze"
          style={{ width: size * 0.48, height: size * 0.48 }}
        />
      </div>
    );
  }
  return (
    <div
      className="flex items-center justify-center text-cmp-bronze-2"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox="0 0 33 33" preserveAspectRatio="xMidYMid meet">
        <path
          d="M28,0H5C2.24,0,0,2.24,0,5v23c0,2.76,2.24,5,5,5h23c2.76,0,5-2.24,5-5V5c0-2.76-2.24-5-5-5ZM29,17c-6.63,0-12,5.37-12,12h-1c0-6.63-5.37-12-12-12v-1c6.63,0,12-5.37,12-12h1c0,6.63,5.37,12,12,12v1Z"
          fill="currentColor"
        />
      </svg>
    </div>
  );
}

function Wordmark({ product, className }: { product: ProductId; className: string }) {
  if (product === 'tinycms' || product === 'atomizer') {
    return (
      <h1 className={`${className} brand-wordmark-with-mark`}>
        <SwallowMark />
        <TinycmsWordmark />
      </h1>
    );
  }
  return <h1 className={className}>{PRODUCT_NAMES[product]}</h1>;
}

/** tinycms hosts (app.tinycms.ai) sign in on their own page; every other host keeps this one. */
export function Login() {
  return isTinycmsFunnel() ? <TinycmsLogin /> : <ProductLogin />;
}

function ProductLogin() {
  useAuthPage(); // Enable natural page scrolling
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [sessionError, setSessionError] = useState<string>('');
  const [sessionInfo, setSessionInfo] = useState<string>('');
  const [isHashing, setIsHashing] = useState<boolean>(false);
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // After login, return to a pending destination (e.g. the WordPress /connect
  // page) if one was stored, otherwise go to the dashboard.
  const redirectAfterAuth = () => {
    const target = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
    navigate(target || '/');
  };

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      redirectAfterAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    // Messages written by non-React code — logout(), the Apollo 401 handler and
    // ErrorBoundary — say something the user needs ("session expired", "you have
    // been logged out"), so they always win.
    const authError = sessionStorage.getItem('authError');
    if (authError) {
      if (authError.includes('logged out')) {
        setSessionInfo(authError);
      } else {
        setSessionError(authError);
      }
      sessionStorage.removeItem('authError');
      return;
    }

    // Otherwise only explain ourselves if we actually turned someone away from a
    // specific page. Arriving at /login directly — or being bounced from the app
    // root, which is just "open the app while signed out" — needs no warning.
    const from = (location.state as { from?: string } | null)?.from;
    if (from && from !== '/') {
      setSessionError('Please log in to access this page');
    }

    // Router state survives a refresh (it lives in history.state), which would
    // re-show the notice on every reload; drop it once it has been read.
    if (from) {
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [loginMutation, { loading, error }] = useMutation<LoginResponse>(LOGIN_MUTATION, {
    onCompleted: (data) => {
      login(data.login);
      redirectAfterAuth();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsHashing(true);

    try {
      const hashedPassword = await hashPasswordClient(password);
      await loginMutation({
        variables: {
          email,
          password: hashedPassword,
        },
      });
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsHashing(false);
    }
  };

  const isSubmitDisabled = loading || isHashing;

  const { product } = useProduct();
  const brand = BRAND[product];
  const features = brand.features;

  return (
    <div className="min-h-screen flex bg-cmp-paper overflow-hidden">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 xl:px-16 relative overflow-hidden">
        {/* Background Pattern — tinted from the brand accent, so the wash turns
            beam gold under Atomizer instead of staying bronze. */}
        <div className="absolute inset-0 opacity-60">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,color-mix(in_srgb,var(--cmp-bronze)_16%,transparent),transparent_50%)]"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,color-mix(in_srgb,var(--cmp-bronze)_10%,transparent),transparent_50%)]"></div>
        </div>

        <div className="relative z-10 max-w-lg">
          {/* Logo & Brand */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <BrandMark product={product} size={64} />
              <div>
                <Wordmark product={product} className="brand-wordmark is-hero mb-1" />
                <p className="text-cmp-ink-3 text-sm">{brand.tagline}</p>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div className="mb-12">
            <h2 className="text-3xl xl:text-4xl font-cmp-display font-normal text-cmp-ink mb-4 leading-tight">
              {brand.headlineLead}
              <br />
              <span className="text-cmp-bronze-2">{brand.headlineAccent}</span>
            </h2>
            <p className="text-cmp-ink-2 text-lg leading-relaxed">{brand.blurb}</p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-cmp2 bg-cmp-surface border border-cmp-line hover:border-cmp-line-2 transition-all group"
                >
                  <div className="w-10 h-10 rounded-cmp2 bg-cmp-bronze-soft flex items-center justify-center transition-colors">
                    <Icon className="w-5 h-5 text-cmp-bronze-2" />
                  </div>
                  <span className="text-cmp-ink text-sm font-medium">{feature.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BrandMark product={product} size={48} />
              <Wordmark product={product} className="brand-wordmark is-md" />
            </div>
            <p className="text-cmp-ink-3 text-sm">{brand.tagline}</p>
          </div>

          {/* Login Card */}
          <div className="bg-cmp-raise p-7 rounded-cmp3 shadow-cmp-e3 border border-cmp-line">
            <div className="mb-8">
              <h2 className="text-2xl font-cmp-display font-normal text-cmp-ink mb-2">
                Welcome Back
              </h2>
              <p className="text-cmp-ink-2">Sign in to continue your journey</p>
            </div>

            {sessionError && (
              <div className="mb-4 p-4 bg-cmp-crit-soft border border-[color-mix(in_srgb,var(--cmp-crit)_40%,transparent)] text-cmp-crit rounded-cmp2">
                {sessionError}
              </div>
            )}

            {sessionInfo && (
              <div className="mb-4 p-4 bg-cmp-ok-soft border border-[color-mix(in_srgb,var(--cmp-ok)_40%,transparent)] text-cmp-ok rounded-cmp2">
                {sessionInfo}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-cmp-ink mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-cmp-surface text-cmp-ink rounded-cmp2 border border-cmp-line focus:outline-none focus:ring-2 focus:ring-cmp-bronze focus:border-transparent transition-all placeholder:text-cmp-ink-3"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-cmp-ink mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-cmp-surface text-cmp-ink rounded-cmp2 border border-cmp-line focus:outline-none focus:ring-2 focus:ring-cmp-bronze focus:border-transparent transition-all placeholder:text-cmp-ink-3"
                />
              </div>
              {error && (
                <div className="p-3 bg-cmp-crit-soft border border-[color-mix(in_srgb,var(--cmp-crit)_30%,transparent)] text-cmp-crit rounded-cmp2 text-sm">
                  {error.message}
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className="ink-btn ink-btn-primary w-full mt-6 px-4 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading || isHashing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-cmp-paper/30 border-t-cmp-paper rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {product === 'tinycms' ? (
              // No self-serve funnel yet: tinycms is granted per account, and
              // the generic signup would create a tinycms account instead.
              <p className="mt-6 pt-6 border-t border-cmp-line text-center text-cmp-ink-2 text-sm">
                tinycms accounts are set up by invitation.
              </p>
            ) : (
              <div className="mt-6 pt-6 border-t border-cmp-line">
                <p className="text-center text-cmp-ink-2 text-sm mb-4">
                  New to {PRODUCT_NAMES[product]}?
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/signup')}
                  className="w-full px-4 py-3 text-cmp-bronze-2 border border-cmp-line-2 rounded-cmp2 font-semibold hover:bg-cmp-bronze-soft hover:border-cmp-bronze transition-all flex items-center justify-center gap-2"
                >
                  Start Your Free Trial
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-cmp-ink-3 text-xs">
            By continuing, you agree to our{' '}
            <a
              href="https://www.tinycms.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.tinycms.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
